/**
 * ForgesListPage
 *
 * Page displaying all Forges in a table with search and link to create.
 */
import { Search, Server } from 'lucide-react';
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import forgeService from '../../services/forgeService';
import { ForgeWithDomain } from '../../types';
import { PageLayout } from '../../components/Layout/PageLayout';
import { Badge } from '../../components/Common/badge';
import TableSkeleton from '../../components/Common/TableSkeleton';

const capitalizeFirst = (str?: string | null): string => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
};

const getStatusBadgeVariant = (status?: string): 'default' | 'secondary' | 'destructive' | 'outline' => {
  if (!status) return 'outline';
  switch (status.toLowerCase()) {
    case 'deployed':
      return 'default';
    case 'creating':
    case 'deploying':
    case 'updating':
      return 'secondary';
    case 'error':
    case 'deleting':
      return 'destructive';
    default:
      return 'outline';
  }
};

const formatDate = (dateStr?: string | null): string => {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '-';
  }
};

const ForgesListPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [forges, setForges] = useState<ForgeWithDomain[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    const fetchForges = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await forgeService.listForgesWithDomains();
        setForges(data);
      } catch (err: any) {
        console.error('Error loading forges:', err);
        setError(t('global.somethingWentWrong', 'Something went wrong'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchForges();
  }, [t]);

  const statusOptions = useMemo(() => {
    const statuses = new Set<string>();
    forges.forEach((f) => {
      if (f.status) statuses.add(f.status);
    });
    return ['all', ...Array.from(statuses).sort()];
  }, [forges]);

  const filteredForges = useMemo(() => {
    const q = query.toLowerCase().trim();
    let list = forges;
    if (q) {
      list = list.filter(
        (f) =>
          (f.display_name || f.name || '').toLowerCase().includes(q) ||
          (f.name || '').toLowerCase().includes(q) ||
          (f.domain_name || '').toLowerCase().includes(q)
      );
    }
    if (statusFilter !== 'all') {
      list = list.filter((f) => (f.status || '').toLowerCase() === statusFilter.toLowerCase());
    }
    return list;
  }, [forges, query, statusFilter]);

  const handleForgeClick = (forgeId: string) => {
    navigate(`/forges/${forgeId}`);
  };

  return (
    <PageLayout
      icon={<Server className="h-6 w-6" />}
      title={t('forge.pages.forgesList', 'Forges')}
      subtitle={t('forge.pages.forgesListDescription', 'View and manage all Forges and their linked Domains')}
    >
      {/* Search + quick filters + New forge card */}
      <div className="rounded-xl border border-border bg-card p-4">
        <div className="flex flex-col gap-3">
          <div className="flex flex-col md:flex-row md:items-center gap-3">
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-muted-foreground" />
              </div>
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('forge.pages.forgesListSearchPlaceholder', 'Search forges or domain...')}
                className="w-full pl-10 pr-3 py-2 rounded-md border border-input bg-background text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              />
            </div>
            <div className="flex flex-wrap items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={() => setStatusFilter('all')}
                className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                  statusFilter === 'all' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                {t('global.all')} ({forges.length})
              </button>
              {statusOptions.filter((s) => s !== 'all').map((status) => {
                const count = forges.filter((f) => (f.status || '').toLowerCase() === status.toLowerCase()).length;
                return (
                  <button
                    key={status}
                    type="button"
                    onClick={() => setStatusFilter(status)}
                    className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                      statusFilter === status ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                  >
                    {status} ({count})
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-border bg-muted/30 p-4 text-muted-foreground">
          {error}
        </div>
      )}

      {/* Table */}
      {!error && (
        <div className="rounded-xl border border-border bg-card p-0 overflow-hidden">
          <div className="overflow-x-auto">
            {isLoading ? (
              <TableSkeleton rows={5} cols={5} />
            ) : filteredForges.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                {query ? t('global.noResults', 'No results') : t('forge.list.noForges', 'No Forges found')}
              </div>
            ) : (
              <table className="min-w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {t('forge.pages.forgesList', 'Forges')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {t('global.status', 'Status')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {t('forge.detail.linkedDomain', 'Linked Etabli Domain')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {t('forge.list.apps', 'Apps')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {t('forge.list.createdAt', 'Created')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border bg-card">
                  {filteredForges.map((forge) => {
                    const appsCount = (forge.system_apps?.length || 0) + (forge.custom_apps?.length || 0);
                    return (
                      <tr
                        key={forge.forge_id}
                        className="cursor-pointer hover:bg-muted/30"
                        onClick={() => handleForgeClick(forge.forge_id)}
                      >
                        <td className="px-4 py-3">
                          <span className="text-sm font-semibold text-foreground">
                            {capitalizeFirst(forge.display_name || forge.name)}
                          </span>
                          {forge.display_name && forge.display_name !== forge.name && (
                            <div className="text-xs text-muted-foreground truncate max-w-xs" title={forge.name}>
                              {forge.name}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {forge.status && (
                            <Badge variant={getStatusBadgeVariant(forge.status)}>{forge.status}</Badge>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-foreground/80">
                          {forge.domain_name || '—'}
                        </td>
                        <td className="px-4 py-3 text-sm text-foreground/80">{appsCount}</td>
                        <td className="px-4 py-3 text-sm text-foreground/80">
                          {formatDate(forge.created_at)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default ForgesListPage;
